// Per-spool labour rollup. Pure rules ported from server/services/excel
// (consolidate.ts + welds.ts) so the Per Spool tab's sub-totals match
// the Consolidated Summary + Excel export exactly. Server export
// remains authoritative for invoicing; this module is the client-side
// mirror for at-a-glance figures inside the project view.
//
// Rules (BSP convention):
//   pipe (plain main, no markers)  → cut, 0 RG, 0 welds (ends covered
//                                    by adjacent fitting/flange items)
//   pipe with "tee"/"tees" in desc → N welds where N = tee count
//                                    (legacy non-D&E drawings where
//                                    the pipe item legitimately spans
//                                    multiple inline tees)
//   pipe rollGroovedEnds=one_end   → 1 weld (branch into main joint)
//   pipe rollGroovedEnds=both_ends → 0 welds (both ends grooved into
//                                    adjacent components, counted there)
//   fitting: elbow/bend/return     → 2 welds
//   fitting: reducer              → 2 welds
//   fitting: tee/branch/socket/
//            weldolet/sockolet/
//            threadolet/cap       → 1 weld
//   fitting (other)               → 1 weld
//   flange (non-blind)            → 1 weld
//   flange (blind, bolted)        → 0 welds
//   stud_drain                    → 1 weld
//   roll_grooved item             → 1 RG operation per qty
//   pipe rollGroovedEnds=one_end  → 1 RG per qty
//   pipe rollGroovedEnds=both_ends → 2 RG per qty
import type { TakeoffItemDto } from "./types/pipe-takeoff";

export interface SpoolLabour {
  pipeMetres: number;
  cuts: number;
  rollGrooves: number;
  welds: number;
}

const ELBOW_LIKE = /elbow|bend|return/;
const REDUCER = /reducer/;
const SINGLE_WELD_FITTING = /tee|branch|socket|weldolet|sockolet|threadolet|cap/;

function fittingWelds(descLower: string): number {
  if (ELBOW_LIKE.test(descLower)) return 2;
  if (REDUCER.test(descLower)) return 2;
  if (SINGLE_WELD_FITTING.test(descLower)) return 1;
  return 1;
}

function pipeWelds(item: TakeoffItemDto): number {
  // Legacy path: non-D&E drawings where a pipe item legitimately spans
  // multiple inline tees with no groove marker. Keep this.
  const teeMatches = (item.description ?? "").match(/\btees?\b/gi)?.length ?? 0;
  if (teeMatches > 0) return teeMatches;
  // Field-based: a branch pipe (one end grooved, joined to a main)
  // contributes 1 weld at the join. Mains with both ends grooved or
  // capped/flanged contribute 0 — those ends are counted by the
  // adjacent components. Description-based "branch" fallback removed;
  // it false-fired on main pipes whose description mentions branches.
  if (item.rollGroovedEnds === "one_end") return 1;
  return 0;
}

function num(v: number | undefined | null): number {
  return v == null ? 0 : Number(v);
}

export function computeSpoolLabour(items: TakeoffItemDto[]): SpoolLabour {
  let pipeMetres = 0;
  let cuts = 0;
  let rollGrooves = 0;
  let welds = 0;

  for (const it of items) {
    const qty = num(it.quantity);
    const desc = it.description ?? "";
    const descLower = desc.toLowerCase();

    if (it.category === "pipe") {
      pipeMetres += qty * num(it.lengthM);
      cuts += qty;
      if (it.rollGroovedEnds === "one_end") rollGrooves += qty;
      else if (it.rollGroovedEnds === "both_ends") rollGrooves += qty * 2;
      welds += qty * pipeWelds(it);
      continue;
    }
    if (it.category === "fitting") {
      welds += qty * fittingWelds(descLower);
      continue;
    }
    if (it.category === "flange") {
      if (descLower.includes("blind")) continue;
      welds += qty;
      continue;
    }
    if (it.category === "stud_drain") {
      welds += qty;
      continue;
    }
    if (it.category === "roll_grooved") {
      rollGrooves += qty;
      continue;
    }
  }

  return { pipeMetres, cuts, rollGrooves, welds };
}
